import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Target,
  Users,
  Globe,
  Award,
  Heart,
  Zap,
  BarChart,
  Wallet,
  Lock,
  ArrowRight,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const About: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Protecting Crypto Assets",
      highlight: "When Markets Fall",
      color: "text-primary",
    },
    {
      main: "Recovery Experts",
      highlight: "When Assets Are Lost",
      color: "text-blue-500",
    },
    {
      main: "Security Innovators",
      highlight: "For Your Digital Wealth",
      color: "text-green-500",
    },
  ];

  // Rotating text effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out-cubic",
      once: false,
    });
  }, []);

  // Intersection observer for stats
  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/about" />

      {/* Hero Section */}
      <section className="container mx-auto px-5 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div
            className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <Shield className="h-4 w-4 mr-2" />
            Our Mission
          </div>

          {/* Rotating Hero Text */}
          <div className="h-48 lg:h-40 mb-8">
            <div
              className={`transition-all duration-500 ${
                fade ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
              }`}
            >
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
                {heroTexts[currentTextIndex].main}
                <span className={`block ${heroTexts[currentTextIndex].color}`}>
                  {heroTexts[currentTextIndex].highlight}
                </span>
              </h1>
            </div>
          </div>

          <p
            className="text-xl text-gray-600 dark:text-gray-400 mb-12"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            At ObeliskRecovery, we combine cutting-edge technology with expert
            recovery services to safeguard your cryptocurrency investments from
            market volatility and security threats.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900/50">
        <div className="container mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p data-aos="fade-right" data-aos-delay="200">
                  Founded in 2022 by a team of blockchain security experts and
                  financial analysts, ObeliskRecovery was born from a simple
                  observation: cryptocurrency investors needed better protection
                  against market downturns and security threats.
                </p>
                <p data-aos="fade-right" data-aos-delay="300">
                  After witnessing millions lost during market crashes and
                  through various scams, our founders developed a dual approach:
                  proactive protection through smart wallet monitoring and
                  reactive recovery through expert intervention.
                </p>
                <p data-aos="fade-right" data-aos-delay="400">
                  Today, we've grown into a comprehensive platform that serves
                  thousands of investors worldwide, successfully protecting and
                  recovering millions in digital assets.
                </p>
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-6"
              data-aos="fade-left"
              data-aos-delay="500"
            >
              <div
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-primary transition-colors"
                data-aos="zoom-in"
                data-aos-delay="100"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Our Mission
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Make cryptocurrency investing safer for everyone through
                  innovative protection and recovery solutions.
                </p>
              </div>
              <div
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-blue-500 transition-colors"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Global Reach
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Serving investors in{" "}
                  {statsInView && <CountUp end={50} suffix="+" duration={2} />}{" "}
                  countries with 24/7 multilingual support.
                </p>
              </div>
              <div
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-green-500 transition-colors"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Award className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Trusted Expertise
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Team of certified blockchain security experts and financial
                  analysts.
                </p>
              </div>
              <div
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-purple-500 transition-colors"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Community First
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Built by and for the crypto community with transparency and
                  trust.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="container mx-auto px-5">
          <div
            className="text-center mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              The principles that guide everything we do at ObeliskRecovery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div
              className="text-center"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Security First
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We implement military-grade encryption and security protocols to
                protect your assets and data at all times.
              </p>
            </div>

            <div
              className="text-center"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Client Success
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Your financial security is our success. We measure our
                achievements by your peace of mind and recovered assets.
              </p>
            </div>

            <div
              className="text-center"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="h-10 w-10 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Innovation
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We continuously develop new technologies and strategies to stay
                ahead of market trends and security threats.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900/50">
        <div className="container mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Advanced Protection Technology
              </h2>
              <div className="space-y-6">
                <div
                  className="flex items-start"
                  data-aos="fade-right"
                  data-aos-delay="200"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <BarChart className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Real-time Market Analysis
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Our AI-powered system monitors market conditions 24/7,
                      identifying potential downturns before they impact your
                      portfolio.
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-start"
                  data-aos="fade-right"
                  data-aos-delay="300"
                >
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Wallet className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Smart Wallet Integration
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Securely connect your wallets with our encrypted system
                      that never stores private keys, only monitors transaction
                      patterns.
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-start"
                  data-aos="fade-right"
                  data-aos-delay="400"
                >
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Lock className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Multi-Layer Security
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      End-to-end encryption, zero-knowledge proofs, and hardware
                      security modules protect your data and transactions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative" data-aos="fade-left" data-aos-delay="500">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
                <div className="space-y-4">
                  <div
                    className="p-4 bg-primary/5 rounded-xl"
                    data-aos="zoom-in"
                    data-aos-delay="100"
                  >
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Protection Protocol
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      When market indicators trigger protection mode, assets are
                      automatically secured based on your preferences.
                    </p>
                  </div>
                  <div
                    className="p-4 bg-green-500/5 rounded-xl"
                    data-aos="zoom-in"
                    data-aos-delay="200"
                  >
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Recovery Success Rate
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span ref={statsRef}>
                        {statsInView && (
                          <CountUp end={78} suffix="%" duration={2} />
                        )}
                      </span>{" "}
                      success rate in recovering funds from scams, hacks, and
                      lost access cases.
                    </p>
                  </div>
                  <div
                    className="p-4 bg-blue-500/5 rounded-xl"
                    data-aos="zoom-in"
                    data-aos-delay="300"
                  >
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Network Support
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Supporting{" "}
                      {statsInView && (
                        <CountUp end={25} suffix="+" duration={2} />
                      )}{" "}
                      blockchain networks including Bitcoin, Ethereum, Solana,
                      and Polygon.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <div className="text-5xl font-bold text-primary mb-2">
                {statsInView && <CountUp end={2022} duration={2} />}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Year Founded
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <div className="text-5xl font-bold text-green-500 mb-2">
                {statsInView && <CountUp end={5000} suffix="+" duration={2} />}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Protected Investors
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              <div className="text-5xl font-bold text-blue-500 mb-2">
                {statsInView && <CountUp end={42} suffix="+" duration={2} />}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Expert Team Members
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="400"
            >
              <div className="text-5xl font-bold text-purple-500 mb-2">
                24/7
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Support Coverage
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 bg-gradient-to-r from-primary to-purple-600"
        data-aos="fade-up"
      >
        <div className="container mx-auto px-5 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Join Our Mission to Make Crypto Safer
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Whether you're protecting existing assets or need help recovering
            lost funds, we're here to help.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <Link
              to="/register"
              className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              Start Protecting Your Assets
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <Link
              to="/login"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-xl font-bold text-lg transition-colors"
            >
              Existing User Login
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="ObeliskRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  ObeliskRecovery
                </span>
              </div>
              <p className="mb-4">
                Secure your crypto assets against market downturns and recover
                lost funds with expert assistance.
              </p>
              <div className="flex space-x-4">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/wallet" className="hover:text-white">
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link to="/recovery" className="hover:text-white">
                    Fund Recovery
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard" className="hover:text-white">
                    Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="hover:text-white">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2">support@obeliskrecovery.com</p>
              <p>24/7 Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p>
              © {new Date().getFullYear()} ObeliskRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
